import '../../common/types/window-augmentations';

import React, { createContext, type ReactNode, useContext, useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { type DocNode } from '@atlaskit/adf-schema';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import type { LabelStack } from '@atlaskit/react-ufo/interaction-context';
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import {
	type ExperienceTrackerAPI,
	useAIMateExperienceTracker,
} from '@atlassian/conversation-assistant-instrumentation/main';
import {
	addSentryBreadcrumb,
	isIsolatedSentryReportingSelected,
	logSentryConsoleWarning,
} from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { ROVO_CHAT_SITES_SOURCE_FILTER_GATE } from '@atlassian/conversation-assistant-service-api/feature-gates';
import { type AgentAction } from '@atlassian/conversation-assistant-service-api/types/agent-actions';
import {
	type AddPromptsToLibraryRequest,
	type AlphaExtendedThinkingMode,
	type Agent,
	type AgentSessionState,
	type AgentDetails,
	type AgentKnowledgeConfigurationResponse,
	type AgentPermissionResponse,
	type AgentVersionInput,
	type AssistanceService,
	type AssistanceServiceConfig,
	type AssistanceServiceProduct,
	type BulkGetPromptsRequest,
	type BulkGetPromptsResponse,
	type BulkUpdatePrivacyRequest,
	type BulkUpdatePrivacyResponse,
	type Chat,
	type ChatMessage,
	type ConversationChannel,
	type ConversationChannelsRequest,
	type ConversationChannelsResponse,
	type PinnedConversationSummaryResponse,
	type ConversationContent,
	type ConversationShareResponse,
	type ConversationStarterResponse,
	type EnsureConversationShareRequest,
	type GetConversationShareMetadataRequest,
	type GetConversationShareMetadataResponse,
	type GetConversationSharePreviewRequest,
	type GetConversationSharePreviewResponse,
	type GetConversationShareResponse,
	type CreatePromptRequest,
	type Dialogues,
	type DynamicConversationStarterRequestNew,
	type DynamicConversationStarterResponseNew,
	type FavoritePromptsRequest,
	type FetchStreamReturn,
	type FileUploadResponse,
	type FileValidationPayload,
	type FileValidationResponse,
	type ForkConversationShareResponse,
	type GroupedSharesResponse,
	type GetAgentPermissionsPayload,
	type GetAllPromptsResponse,
	type GetPromptResponse,
	isHumanMessage,
	type NLCreateAgentPayload,
	type NLCreateAgentStreamMessage,
	type PostAgentPayload,
	type RawChat,
	type RovoLabsChatStreamPayload,
	type RovoLabsConfirmedTools,
	type RovoLabsDefaultPrompt,
	type RovoLabsLanguageModel,
	type RovoLabsTool,
	type CreateMemoryRequest,
	type MemoryEditPromptResponse,
	type RovoMemoriesResponse,
	type RovoMemoryEditsResponse,
	type ContentType,
	type RovoMemoryEditsCountResponse,
	type RovoUserSettings,
	type SuggestedPromptTile,
	type SuggestedPromptTileStreamMessage,
	type SuggestedPromptsResponse,
	type SendMessageBrowserContext,
	type SendMessageDashboardContext,
	type SendMessageDashboardEditorContext,
	type SendMessageDatabaseContext,
	type SendMessageEditorContext,
	type SendMessageSearchArtifact,
	type SendMessageSlidesContext,
	type SendMessageWhiteboardContext,
	type SendMessageWorkflowContext,
	type WritingStyleContext,
	type StreamMessage,
	type UpdatePromptRequest,
	type UpsertConversationShareRequest,
	type ReplaceConversationShareRequest,
	type WebSearchConfigurationResponse,
	type ApiTask,
	type ApiTaskListResponse,
	type ApiTaskSSEUpdate,
	type RovoMemoryBlurbResponse,
	type RovoChatSourceMode,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type BulkMessageActionPayload,
	type BulkMessageActionResponse,
	type MessageActionPayload,
	type MessageActionResponse,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { getTraceIdFromResponse } from '@atlassian/conversation-assistant-utils/httpUtils';
import { normalizeRequestRoute } from '@atlassian/conversation-assistant-utils/request-observer';

import { setRovoChatProduct } from '../../common/utils/product-config';

import { buildMessageStreamRequestContext } from './buildMessageStreamRequestContext';
import { resolveMessageStreamBrowserUrl } from './resolveMessageStreamBrowserUrl';
import { readJsonStream } from './read-json-stream';
import { readSSEStream } from './read-sse-stream';
import { buildEffectiveCapabilities } from './utils/capabilities';
import { addPath, processResponse } from './utils/client';
import { generateConversationName } from './utils/conversation';
import { toStreamResponse } from './utils/data';
type FetchOptions = {
	path: string;
	options: RequestInit;
	holdName?: string;
};

type ConversationChannelResponse = {
	agent_session_state?: unknown;
	[key: string]: unknown;
};

type StreamLifecycle =
	| 'started'
	| 'firstData'
	| 'completed'
	| 'cancelled'
	| 'abort'
	| 'timedOut'
	| 'error';

const addRequestBreadcrumb = ({
	durationMs,
	lifecycle,
	options,
	status,
}: {
	durationMs?: number;
	lifecycle: StreamLifecycle | 'success' | 'httpError' | 'networkError';
	options: FetchOptions;
	status?: number;
}): void => {
	void addSentryBreadcrumb({
		category: 'request',
		data: {
			durationMs: durationMs === undefined ? undefined : Math.round(durationMs),
			lifecycle,
			method: (options.options.method ?? 'GET').toUpperCase(),
			operation: options.holdName,
			route: normalizeRequestRoute(options.path),
			status,
		},
		message: `${options.options.method ?? 'GET'} ${
			options.holdName ?? normalizeRequestRoute(options.path)
		} ${lifecycle}`,
		type: 'http',
	});
};

const observeStreamLifecycle = async function* <T>(
	generator: AsyncGenerator<T>,
	options: FetchOptions,
): AsyncGenerator<T> {
	const startedAt = performance.now();
	let firstDataReceived = false;
	let lifecycleEnded = false;
	addRequestBreadcrumb({ lifecycle: 'started', options });

	try {
		for await (const value of generator) {
			if (!firstDataReceived) {
				firstDataReceived = true;
				addRequestBreadcrumb({
					durationMs: performance.now() - startedAt,
					lifecycle: 'firstData',
					options,
				});
			}
			yield value;
		}
		lifecycleEnded = true;
		addRequestBreadcrumb({
			durationMs: performance.now() - startedAt,
			lifecycle: options.options.signal?.aborted ? 'abort' : 'completed',
			options,
		});
	} catch (error) {
		lifecycleEnded = true;
		addRequestBreadcrumb({
			durationMs: performance.now() - startedAt,
			lifecycle:
				error instanceof Error && error.name === 'AbortError'
					? 'abort'
					: error instanceof Error && /timeout/i.test(error.name)
						? 'timedOut'
						: 'error',
			options,
		});
		throw error;
	} finally {
		if (!lifecycleEnded) {
			addRequestBreadcrumb({
				durationMs: performance.now() - startedAt,
				lifecycle: options.options.signal?.aborted ? 'abort' : 'cancelled',
				options,
			});
		}
	}
};

const PRODUCT_HEADER = 'x-product';

/** Number of tasks to request per page when using cursor-based pagination. */
const TASKS_PAGE_SIZE = 25;
const EXPERIENCE_ID_HEADER = 'x-experience-id';
const CHANNEL_ID_HEADER = 'X-Channel-Id';
const CLOUD_ID_HEADER = 'x-cloudid';

const AGENT_SESSION_STATE_VALUES = new Set<unknown>([
	'WORKING',
	'INPUT_REQUIRED',
	'ARTEFACT_READY',
	'AUTH_REQUIRED',
	'FAILED',
	'REJECTED',
	'COMPLETED',
	'CANCELLED',
	'UNKNOWN',
] satisfies AgentSessionState[]);

const isAgentSessionState = (value: unknown): value is AgentSessionState =>
	AGENT_SESSION_STATE_VALUES.has(value);

const hasCloudIdHeader = (headers: Record<string, string>) =>
	Object.keys(headers).some((name) => name.toLowerCase() === CLOUD_ID_HEADER);

const DEFAULT_FILE_VALIDATE_PATH = '/rovo/v1/chat/message/file/validate';
const DEFAULT_FILE_UPLOAD_PATH = '/rovo/v1/chat/message/file';

type PrivateAssistanceServiceConfig = AssistanceServiceConfig &
	Required<Pick<AssistanceServiceConfig, 'baseUrl' | 'headers'>>;

export abstract class AssistanceServiceBase {
	protected config: PrivateAssistanceServiceConfig;

	public getConfig = (): {
		baseUrl: string;
		headers: Record<string, string>;
		product: AssistanceServiceProduct;
		credentials?: RequestInit['credentials'];
		experienceId: string;
		siteId?: string;
		channelId?: string;
		minionAlias?: string;
		labelStack?: LabelStack;
		fileValidatePath?: string;
		fileUploadPath?: string;
	} => {
		return {
			...this.config,
		};
	};

	constructor(props: AssistanceServiceConfig) {
		const defaultBaseUrl = '/gateway/api/assist';

		const defaultConfig = {
			baseUrl: defaultBaseUrl,
			headers: {},
		};

		this.config = {
			...props,
			baseUrl: props.baseUrl || defaultConfig.baseUrl,
			credentials: props.credentials,
			headers: {
				...(props.headers || defaultConfig.headers),
			},
		};
	}

	protected createHeaders(init?: HeadersInit): Headers {
		const configHeaders = this.config.headers || {};
		return new Headers({
			[PRODUCT_HEADER]: this.config.product,
			[EXPERIENCE_ID_HEADER]: this.config.experienceId,
			...(this.config.siteId &&
			fg('rovo_chat_site_switch_reset') &&
			!hasCloudIdHeader(configHeaders)
				? { [CLOUD_ID_HEADER]: this.config.siteId }
				: {}),
			...configHeaders,
			...(this.config.channelId && fg('jira-ai-agent-sessions-channel-id-fix')
				? { [CHANNEL_ID_HEADER]: this.config.channelId }
				: {}),
			...init,
		});
	}

	protected getChannelId(defaultChannelId: string): string {
		return this.config.channelId || defaultChannelId;
	}

	protected async fetch({ path, options, holdName }: FetchOptions): Promise<Response> {
		const { baseUrl: configBaseUrl, credentials: configCredentials } = this.config;
		const baseUrl = configBaseUrl;
		const requestUrl = addPath(baseUrl, path);

		const startTime = performance.now();
		if (!isIsolatedSentryReportingSelected()) {
			const response = await window.fetch(requestUrl, {
				credentials: configCredentials,
				...options,
			});

			if (this.config.labelStack) {
				// Please note that the recorded duration of this hold may differ from the time spent actually fetching the data.
				// This is because the browser may schedule network calls and JavaScript execution at its own discrection based
				// on available resources, the main thread being blocked, etc... Still, it is a helpful approximation.
				const endTime = performance.now();
				addSpanToAll(
					'fetch',
					holdName ? `${options.method}-${holdName}` : 'unknown',
					this.config.labelStack,
					startTime,
					endTime,
				);
			}

			return processResponse({ response });
		}

		try {
			const response = await window.fetch(requestUrl, {
				credentials: configCredentials,
				...options,
			});

			if (this.config.labelStack) {
				// Please note that the recorded duration of this hold may differ from the time spent actually fetching the data.
				// This is because the browser may schedule network calls and JavaScript execution at its own discrection based
				// on available resources, the main thread being blocked, etc... Still, it is a helpful approximation.
				const endTime = performance.now();
				addSpanToAll(
					'fetch',
					holdName ? `${options.method}-${holdName}` : 'unknown',
					this.config.labelStack,
					startTime,
					endTime,
				);
			}

			const processedResponse = await processResponse({ response });
			addRequestBreadcrumb({
				durationMs: performance.now() - startTime,
				lifecycle: 'success',
				options: { path, options, holdName },
				status: response.status,
			});
			return processedResponse;
		} catch (error) {
			addRequestBreadcrumb({
				durationMs: performance.now() - startTime,
				lifecycle:
					error instanceof ApiError
						? 'httpError'
						: error instanceof Error && error.name === 'AbortError'
							? 'abort'
							: 'networkError',
				options: { path, options, holdName },
				status: error instanceof ApiError ? error.errorAttributes.statusCode : undefined,
			});
			throw error;
		}
	}

	protected async fetchJson<T>(options: FetchOptions): Promise<T> {
		const response = await this.fetch(options);
		const json = await response.json();
		return json;
	}

	public async getConversationSharePreview(
		_payload: GetConversationSharePreviewRequest,
	): Promise<GetConversationSharePreviewResponse> {
		throw new ApiError('Share preview not found', {
			statusCode: 404,
		});
	}

	public async getConversationShareMetadata(
		_payload: GetConversationShareMetadataRequest,
	): Promise<GetConversationShareMetadataResponse> {
		throw new ApiError('Share metadata not found', {
			statusCode: 404,
		});
	}

	// Overload: useSSE false or not provided - yields T only (JSON stream)
	protected async fetchStream<T>(
		options: FetchOptions,
		config?: { useSSE?: false },
	): Promise<FetchStreamReturn<T, false>>;

	// Overload: useSSE true - yields ParsedSSEMessage<T> or T (JSON fallback)
	protected async fetchStream<T>(
		options: FetchOptions,
		config: { useSSE: true },
	): Promise<FetchStreamReturn<T, true>>;

	// Overload: useSSE boolean (dynamic) - yields union type for type safety
	protected async fetchStream<T>(
		options: FetchOptions,
		config: { useSSE?: boolean },
	): Promise<FetchStreamReturn<T, boolean>>;

	protected async fetchStream<T>(
		options: FetchOptions,
		config?: { useSSE?: boolean },
	): Promise<FetchStreamReturn<T, boolean>> {
		const response = await this.fetch(options);
		const contentType = response.headers.get('content-type') ?? '';
		const headerSuggestsSSE = config?.useSSE && contentType.includes('text/event-stream');
		const shouldObserveStream = isIsolatedSentryReportingSelected();

		return {
			responseData: {
				traceId: getTraceIdFromResponse(response),
			},
			generator: headerSuggestsSSE
				? shouldObserveStream
					? observeStreamLifecycle(readSSEStream<T>(response), options)
					: readSSEStream<T>(response)
				: shouldObserveStream
					? observeStreamLifecycle(readJsonStream<T>(response), options)
					: readJsonStream<T>(response),
		};
	}
}

export class AssistanceServiceImpl extends AssistanceServiceBase implements AssistanceService {
	constructor(props: AssistanceServiceConfig) {
		super(props);
	}

	public async sendMessageStream({
		agentNamedId,
		agentId,
		message,
		mimeType,
		conversationId,
		controller,
		storeMessage = false,
		citationsEnabled = true,
		aiFeatureContext,
		jiraWorkflowContext,
		editorContext,
		whiteboardContext,
		slidesContext,
		databaseContext,
		browserContext,
		additionalContext,
		writingStyleContext,
		dashboardContext,
		dashboardEditorContext,
		fileIds,
		webSearchEnabled,
		sourceMode,
		deepResearchEnabled,
		thinkDeeperEnabled,
		alphaExtendedThinkingMode,
		fastModeEnabled,
		planModeEnabled,
		appFilters,
		selectedCloudIds,
		useCurrentBrowsingContext = undefined,
		minionAliasConfig,
		optInToDeepResearchV2 = false,
		optInThinkDeeperMessages = false,
		optInAnsNow = false,
		optInNewToolTraces = false,
		optInNewTeamworkTraceIcons = false,
		browserContextSanitizer,
		optInSSEStream = false,
		agentVersion,
		spaceId,
		capabilities,
		shouldExtractConversationScopes,
		searchArtifact,
		parentMessageId,
	}: {
		conversationId: string;
		message: string | DocNode;
		mimeType?: string;
		agentNamedId?: string;
		agentId?: string;
		controller?: AbortController;
		storeMessage?: boolean;
		citationsEnabled?: boolean;
		aiFeatureContext?: Record<string, unknown>;
		jiraWorkflowContext?: SendMessageWorkflowContext;
		editorContext?: SendMessageEditorContext;
		whiteboardContext?: SendMessageWhiteboardContext;
		slidesContext?: SendMessageSlidesContext;
		databaseContext?: SendMessageDatabaseContext;
		browserContext?: SendMessageBrowserContext;
		additionalContext?: Record<string, unknown>;
		writingStyleContext?: WritingStyleContext;
		dashboardContext?: SendMessageDashboardContext;
		dashboardEditorContext?: SendMessageDashboardEditorContext;
		fileIds?: string[];
		webSearchEnabled?: boolean;
		sourceMode?: RovoChatSourceMode;
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
		fastModeEnabled?: boolean;
		planModeEnabled?: boolean;
		appFilters?: string[];
		selectedCloudIds?: string[];
		useCurrentBrowsingContext?: boolean;
		minionAliasConfig?: Record<string, unknown>;
		optInToDeepResearchV2?: boolean;
		optInThinkDeeperMessages?: boolean;
		optInAnsNow?: boolean;
		optInNewToolTraces?: boolean;
		optInNewTeamworkTraceIcons?: boolean;
		browserContextSanitizer?: {
			isBlocklisted: (url: string) => boolean;
		};
		optInSSEStream?: boolean;
		agentVersion?: AgentVersionInput;
		spaceId?: string;
		capabilities?: string[];
		shouldExtractConversationScopes?: boolean;
		searchArtifact?: SendMessageSearchArtifact;
		parentMessageId?: string;
	}): Promise<FetchStreamReturn<StreamMessage, boolean>> {
		const effectiveCapabilities = buildEffectiveCapabilities(capabilities);
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
			[CHANNEL_ID_HEADER]: this.getChannelId('rovo-chat'),
			// This header is used to opt-in to Deep Research v2 on the BE.
			// This is to ensure that we don't use v2 deep research when we are using the old Rovo Chat UI prior to the refresh..
			...(optInToDeepResearchV2 && { 'X-Deep-Research-V2-Opt-In': 'true' }),
			// Opt in to using RESEARCH_STEP, RESEARCH_PLAN, and similar messages for "think deeper".
			// Ensures backward compatibility with legacy clients and allows application targeting within the experiment.
			...(optInThinkDeeperMessages && { 'X-Think-Deeper-Messages-Opt-In': 'true' }),
			...(optInAnsNow && { 'X-Answer-Now-UI-Opt-In': 'true' }),
			...(optInNewToolTraces && { 'X-New-Tool-Traces-Opt-In': 'true' }),
			...(optInNewTeamworkTraceIcons && { 'X-New-Teamwork-Trace-Icons-Opt-In': 'true' }),
			...(effectiveCapabilities &&
				effectiveCapabilities.length > 0 && {
					'X-Rovo-Chat-Capabilities': JSON.stringify(effectiveCapabilities),
				}),
		});
		let knowledgeOverride = {};
		// not setting knowledgeEnabled and filters means all org knowledge is applied by default.
		if (appFilters) {
			if (appFilters.length === 0) {
				knowledgeOverride = {
					knowledgeEnabled: false,
				};
			} else {
				knowledgeOverride = {
					knowledgeEnabled: true,
					filters: {
						knowledgeSources: appFilters,
					},
				};
			}
		}
		const selectedCloudIdsOverride =
			fg(ROVO_CHAT_SITES_SOURCE_FILTER_GATE) && selectedCloudIds && selectedCloudIds.length > 0
				? {
						selectedCloudIds: selectedCloudIds.map((cloudId) => ({ cloudId })),
					}
				: {};
		const { browserUrl: url, isBrowserContextBlocklisted } = resolveMessageStreamBrowserUrl({
			browserContext,
			browserContextSanitizer,
			product: this.getConfig().product,
		});
		const defaultMimeType = typeof message === 'string' ? 'text/markdown' : 'text/adf';
		const body = {
			content: message,
			context: buildMessageStreamRequestContext({
				browserUrl: url,
				useCurrentBrowsingContext,
				webSearchEnabled,
				sourceMode,
				deepResearchEnabled,
				thinkDeeperEnabled,
				alphaExtendedThinkingMode,
				fastModeEnabled,
				planModeEnabled,
				editorContext,
				whiteboardContext,
				slidesContext,
				databaseContext,
				dashboardEditorContext,
				browserContext,
				isBrowserContextBlocklisted,
				jiraWorkflowContext,
				additionalContext,
				writingStyleContext,
				dashboardContext,
				aiFeatureContext,
				fileIds,
				knowledgeOverride,
				selectedCloudIdsOverride,
				searchArtifact,
				shouldExtractConversationScopes,
			}),
			recipient_agent_named_id: agentNamedId,
			agent_id: agentId,
			mimeType: mimeType && fg('jira_ai_rendering_a2ui') ? mimeType : defaultMimeType,
			...(agentVersion ? { agent_version: agentVersion } : {}),
			// don't allow store message if feature flag is off
			store_message: fg('ai_mate_show_store_messages') ? storeMessage : false,
			citations_enabled: citationsEnabled,
			...(minionAliasConfig ??
				(this.getConfig().minionAlias
					? { stratus_config: { minion_alias: this.getConfig().minionAlias } }
					: {})),
			...(spaceId ? { space_id: spaceId } : {}),
			...(parentMessageId ? { parent_message_id: parentMessageId } : {}),
		};
		const options: RequestInit = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify(body),
			signal: controller?.signal,
		};

		return this.fetchStream<StreamMessage>(
			{
				path: optInSSEStream
					? `/rovo/v1/chat/conversation/${conversationId}/message/stream/sse`
					: `/rovo/v1/chat/conversation/${conversationId}/message/stream`,
				options,
			},
			{ useSSE: optInSSEStream },
		);
	}

	public async reconnectToMessageStream({
		conversationId,
		messageId,
		lastEventId,
		controller,
	}: {
		conversationId: string;
		messageId: string;
		lastEventId?: string;
		controller?: AbortController;
	}): Promise<FetchStreamReturn<StreamMessage, true>> {
		const headers = this.createHeaders();

		// Add Last-Event-ID header if available
		if (lastEventId) {
			headers.set('Last-Event-ID', lastEventId);
		}

		const options: RequestInit = {
			method: 'GET',
			headers,
			signal: controller?.signal,
		};

		return this.fetchStream<StreamMessage>(
			{
				path: `/rovo/v1/chat/conversation/${conversationId}/message/${messageId}/stream/sse`,
				options,
			},
			{ useSSE: true },
		);
	}

	// Only takes effect if the stream is a Deep Research stream
	public async cancelMessageStream({
		conversationId,
		messageId,
	}: {
		conversationId: string;
		messageId: string;
	}): Promise<void> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});

		const options: RequestInit = {
			method: 'POST',
			headers,
		};

		await this.fetch({
			path: `/rovo/v1/chat/conversation/${conversationId}/message/${messageId}/cancel`,
			options,
		});
	}

	public async sendMessageConfirmedTools({
		conversationId,
		agentId,
		controller,
		confirmed_tools,
		optInSSEStream = false,
		storeMessage = false,
		webSearchEnabled,
		deepResearchEnabled,
		thinkDeeperEnabled,
		alphaExtendedThinkingMode,
		fastModeEnabled,
		planModeEnabled,
		aiFeatureContext,
		agentVersion,
		optInToDeepResearchV2 = false,
		optInThinkDeeperMessages = false,
		optInAnsNow = false,
		optInNewToolTraces = false,
		optInNewTeamworkTraceIcons = false,
		capabilities,
		searchArtifact,
		jiraWorkflowContext,
		editorContext,
		whiteboardContext,
		slidesContext,
		databaseContext,
		browserContext,
		additionalContext,
		dashboardContext,
		dashboardEditorContext,
		useCurrentBrowsingContext = undefined,
		browserContextSanitizer,
	}: {
		conversationId: string;
		agentId?: string;
		controller?: AbortController;
		confirmed_tools: RovoLabsConfirmedTools;
		optInSSEStream?: boolean;
		storeMessage?: boolean;
		webSearchEnabled?: boolean;
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
		fastModeEnabled?: boolean;
		planModeEnabled?: boolean;
		aiFeatureContext?: Record<string, unknown>;
		agentVersion?: AgentVersionInput;
		optInToDeepResearchV2?: boolean;
		optInThinkDeeperMessages?: boolean;
		optInAnsNow?: boolean;
		optInNewToolTraces?: boolean;
		optInNewTeamworkTraceIcons?: boolean;
		capabilities?: string[];
		searchArtifact?: SendMessageSearchArtifact;
		jiraWorkflowContext?: SendMessageWorkflowContext;
		editorContext?: SendMessageEditorContext;
		whiteboardContext?: SendMessageWhiteboardContext;
		slidesContext?: SendMessageSlidesContext;
		databaseContext?: SendMessageDatabaseContext;
		browserContext?: SendMessageBrowserContext;
		additionalContext?: Record<string, unknown>;
		dashboardContext?: SendMessageDashboardContext;
		dashboardEditorContext?: SendMessageDashboardEditorContext;
		useCurrentBrowsingContext?: boolean;
		browserContextSanitizer?: {
			isBlocklisted: (url: string) => boolean;
		};
	}): Promise<FetchStreamReturn<StreamMessage>> {
		const effectiveCapabilities = buildEffectiveCapabilities(capabilities);
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
			[CHANNEL_ID_HEADER]: this.getChannelId('rovo-chat'),
			...(optInToDeepResearchV2 && { 'X-Deep-Research-V2-Opt-In': 'true' }),
			...(optInThinkDeeperMessages && { 'X-Think-Deeper-Messages-Opt-In': 'true' }),
			...(optInAnsNow && { 'X-Answer-Now-UI-Opt-In': 'true' }),
			...(optInNewToolTraces && { 'X-New-Tool-Traces-Opt-In': 'true' }),
			...(optInNewTeamworkTraceIcons && { 'X-New-Teamwork-Trace-Icons-Opt-In': 'true' }),
			...(effectiveCapabilities &&
				effectiveCapabilities.length > 0 && {
					'X-Rovo-Chat-Capabilities': JSON.stringify(effectiveCapabilities),
				}),
		});
		// browser_url is historically the current window location. The blocklist gates the page
		// browser context added under the `rovo-include-full-context-on-confirmed-tools`
		// experiment (when a browserContext is supplied). The NO-URL sentinel under the
		// `jira_free_gen_editor` experiment suppresses the URL (matching sendMessageStream) so that
		// cohort does not leak a URL.
		const browserUrl = browserContext?.context?.browserUrl;
		const isBrowserContextBlocklisted = !!(
			browserUrl && browserContextSanitizer?.isBlocklisted(browserUrl)
		);
		const isBrowserUrlSuppressed =
			browserUrl === 'NO-URL' &&
			FeatureGates.getExperimentValue('jira_free_gen_editor', 'isEnabled', false);
		const url = isBrowserUrlSuppressed ? '' : window?.location?.href;
		const isBrowserSelectionContextGateEnabled = fg('rovo_chat_browser_selection_context');
		const browserSelection = isBrowserSelectionContextGateEnabled
			? browserContext?.context?.selection
			: undefined;

		const body = {
			conversation_id: conversationId,
			agent_id: agentId,
			confirmed_tools,
			...(agentVersion ? { agent_version: agentVersion } : {}),

			context: {
				// Historical slim context — unchanged so the experiment-off request is byte-identical.
				browser_url: url,
				webSearchEnabled,
				...(deepResearchEnabled ? { deepResearchEnabled } : {}),
				...(thinkDeeperEnabled ? { extendedThinkingEnabled: thinkDeeperEnabled } : {}),
				...(alphaExtendedThinkingMode ? { alphaExtendedThinkingMode } : {}),
				...(planModeEnabled
					? {
							planModeEnabled,
							alphaExtendedThinkingMode: 'MARATHON',
							extendedThinkingEnabled: true,
						}
					: {}),
				...(fastModeEnabled ? { fastModeEnabled } : {}),
				...(aiFeatureContext ? { aiFeatureContext } : {}),
				...(searchArtifact ? { search_artifact: searchArtifact } : {}),
				// Full request context — supplied by the store action only when the
				// `rovo-include-full-context-on-confirmed-tools` experiment is on, so the backend
				// receives the same page/whiteboard/creation context as a regular sendMessage.
				// Every field is omitted when undefined, so the slim request above is unaffected.
				...(useCurrentBrowsingContext === undefined ? {} : { useCurrentBrowsingContext }),
				// Content-context objects. Inlined to mirror sendMessageStream's serialization
				// exactly; a unit test (the drift guard) asserts the two stay in sync. Each is
				// omitted when undefined, so the slim request above is unaffected.
				...(editorContext ? { editor: editorContext } : {}),
				...(whiteboardContext ? { whiteboardContext } : {}),
				...(slidesContext ? { slidesContext } : {}),
				...(databaseContext ? { databaseContext } : {}),
				...(dashboardEditorContext ? { dashboardEditorContext } : {}),
				...(browserContext && !isBrowserContextBlocklisted
					? {
							browser: {
								htmlBody: browserContext.context?.htmlBody,
								canvasText: browserContext.context?.canvasText,
								...(browserSelection ? { selection: browserSelection } : {}),
							},
						}
					: {}),
				jiraWorkflowContext,
				...additionalContext,
				dashboardContext,
			},

			// These values are hardcoded because they are not used in backend, but required in the contract
			content: '',
			mimeType: 'text/markdown',
			// don't allow store message if feature flag is off
			store_message: fg('ai_mate_show_store_messages') ? storeMessage : false,
			citations_enabled: false,
		};
		const options: RequestInit = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify(body),
			signal: controller?.signal,
		};

		return this.fetchStream<StreamMessage>(
			{
				path: optInSSEStream
					? `/rovo/v1/chat/conversation/${conversationId}/message/stream/sse`
					: `/rovo/v1/chat/conversation/${conversationId}/message/stream`,
				options,
			},
			{ useSSE: optInSSEStream },
		);
	}

	public async nlCreateAgentStream({
		controller,
		latest_user_message,
		draft_agent,
		conversation_history,
	}: {
		controller?: AbortController;
	} & NLCreateAgentPayload): Promise<FetchStreamReturn<NLCreateAgentStreamMessage>> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const body = {
			latest_user_message,
			draft_agent,
			conversation_history,
		};
		const options: RequestInit = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify(body),
			signal: controller?.signal,
		};

		return this.fetchStream<NLCreateAgentStreamMessage>({
			path: `/rovo/v1/agents/nlcreate`,
			options,
		});
	}

	public async getChat(conversationId: string, capabilities?: string[]): Promise<Chat> {
		// Forward the caller-provided chat capabilities (e.g. `inline_lumina_enabled`,
		// which is gated behind `rovo_chat_enable_inline_lumina`) so that fetching a
		// chat from history renders the same rich (Lumina) components as a live stream,
		// rather than downgrading to basic markdown.
		//
		// Note: this intentionally forwards `capabilities` as-is rather than routing
		// through `buildEffectiveCapabilities`. That helper fires the actionable
		// follow-ups experiment exposure, which must only happen when a message is
		// SENT — not when history is read — so reusing it here would pollute the
		// experiment and attach send-only capabilities to a read request.
		const headers = this.createHeaders({
			...(capabilities &&
				capabilities.length > 0 && {
					'X-Rovo-Chat-Capabilities': JSON.stringify(capabilities),
				}),
		});
		const options = {
			method: 'GET',
			headers: headers,
		};

		const params = new URLSearchParams({
			shouldMergeA2UI: 'true',
		});

		const path = expVal('jira_ai_merging_a2ui_history', 'isEnabled', false)
			? `/rovo/v1/chat/conversation/${conversationId}/messages?${params}`
			: `/rovo/v1/chat/conversation/${conversationId}/messages`;

		const rawChat = await this.fetchJson<{
			items: RawChat;
		}>({
			path,
			options,
			holdName: 'rovo-v1-chat-conversation-[conversationId]-messages',
		});
		const chatArray = rawChat.items;
		return chatArray.map(
			(message): ChatMessage => (isHumanMessage(message) ? message : toStreamResponse(message)),
		);
	}

	public async deleteChat(conversationId: string, controller?: AbortController): Promise<void> {
		const headers = this.createHeaders();
		const options = {
			method: 'DELETE',
			headers: headers,
			signal: controller?.signal,
		};
		await this.fetch({
			path: `/rovo/v1/chat/conversation/${conversationId}`,
			options,
			holdName: 'rovo-v1-chat-conversation-[conversationId]',
		});
	}

	public async getAgents(controller?: AbortController): Promise<Agent[]> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers: headers,
			signal: controller?.signal,
		};
		const response = await this.fetchJson<{
			items: Agent[];
		}>({
			path: '/rovo/v1/agents',
			options,
			holdName: 'rovo-v1-agents',
		});

		return response.items;
	}

	public async getAgentDetails(id: string, controller?: AbortController): Promise<AgentDetails> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers: headers,
			signal: controller?.signal,
		};
		return this.fetchJson<AgentDetails>({
			path: `/rovo/v1/agents/${id}`,
			options,
			holdName: 'rovo-v1-agents-[id]',
		});
	}

	public async getAgentDetailsByIdentityAccountId(
		identityAccountId: string,
		controller?: AbortController,
	): Promise<AgentDetails> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers: headers,
			signal: controller?.signal,
		};
		return this.fetchJson<AgentDetails>({
			path: `/rovo/v1/agents/accountid/${identityAccountId}`,
			options,
			holdName: 'rovo-v1-agents-accountid-[identityAccountId]',
		});
	}

	public async createAgent(
		payload: PostAgentPayload,
		controller?: AbortController,
	): Promise<Agent> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify(payload),
			signal: controller?.signal,
		};
		return this.fetchJson<Agent>({
			path: '/rovo/v1/agents',
			options,
			holdName: 'rovo-v1-agents',
		});
	}

	public async updateAgent(
		id: string,
		payload: PostAgentPayload,
		controller?: AbortController,
	): Promise<Agent> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'PUT',
			headers: headers,
			body: JSON.stringify(payload),
			signal: controller?.signal,
		};
		const response = await this.fetchJson<Agent>({
			path: `/rovo/v1/agents/${id}`,
			options,
			holdName: 'rovo-v1-agents-[id]',
		});

		return response;
	}

	public async deleteAgent(id: string, controller?: AbortController): Promise<void> {
		const headers = this.createHeaders();
		const options = {
			method: 'DELETE',
			headers: headers,
			signal: controller?.signal,
		};
		return this.fetchJson<void>({
			path: `/rovo/v1/agents/${id}`,
			options,
			holdName: 'rovo-v1-agents-[id]',
		});
	}

	public async favouriteAgent(id: string, controller?: AbortController): Promise<void> {
		const headers = this.createHeaders();
		const options = {
			method: 'POST',
			headers: headers,
			signal: controller?.signal,
		};
		this.fetch({
			path: `/rovo/v1/agents/${id}/favourite`,
			options,
			holdName: 'rovo-v1-agents-[id]-favourite',
		});
	}

	public async unfavouriteAgent(id: string, controller?: AbortController): Promise<void> {
		const headers = this.createHeaders();
		const options = {
			method: 'DELETE',
			headers: headers,
			signal: controller?.signal,
		};
		this.fetch({
			path: `/rovo/v1/agents/${id}/favourite`,
			options,
			holdName: 'rovo-v1-agents-[id]-favourite',
		});
	}

	public async createConversationChannel(options?: {
		name?: string | null;
		dialogues?: Dialogues[];
		tags?: string[];
		agentId?: string;
	}): Promise<ConversationChannel> {
		const name = options && 'name' in options ? options.name : generateConversationName();

		return this.fetchJson<ConversationChannel>({
			path: `/rovo/v1/chat/conversation`,
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
					[CHANNEL_ID_HEADER]: this.getChannelId('rovo-chat'),
				}),
				body: JSON.stringify({
					name,
					dialogues: options?.dialogues ?? undefined,
					tags: options?.tags,
					agent_id: options?.agentId,
				}),
			},
			holdName: 'rovo-v1-chat-conversation',
		});
	}

	public async getConversationChannels(
		options?: ConversationChannelsRequest,
	): Promise<ConversationChannelsResponse> {
		const searchParams = new URLSearchParams();
		if (options?.limit !== undefined) {
			searchParams.set('limit', String(options.limit));
		}
		if (options?.cursor !== undefined) {
			searchParams.set('cursor', options.cursor);
		}
		const queryString = searchParams.toString();

		const result = await this.fetchJson<{
			items: ConversationChannelResponse[];
			hasNext?: boolean;
			cursor?: string | null;
		}>({
			path: `/rovo/v1/chat/conversations${queryString ? `?${queryString}` : ''}`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-chat-conversations',
		});

		// Transform snake_case fields to camelCase
		return {
			items: result.items.map((item) => this.transformSnakeCaseToCamelCase(item)),
			hasNext: result.hasNext ?? false,
			cursor: result.cursor ?? null,
		};
	}

	public async getPinnedConversationSummary(): Promise<
		PinnedConversationSummaryResponse | undefined
	> {
		try {
			const result = await this.fetchJson<{
				pinned_count?: number;
				max_pinned_conversations?: number;
			}>({
				path: '/rovo/v1/chat/conversations/pinned-summary',
				options: {
					method: 'GET',
					headers: this.createHeaders(),
				},
				holdName: 'rovo-v1-chat-conversations-pinned-summary',
			});

			return {
				pinnedCount: result.pinned_count ?? 0,
				maxPinnedConversations: result.max_pinned_conversations ?? 0,
			};
		} catch (error) {
			// The endpoint not being deployed yet surfaces as a 404 — ignore that (callers fall back to
			// the local pinned count). Any other failure is genuine, so rethrow rather than swallow it
			// silently, letting the caller report/handle it.
			const statusCode = error instanceof ApiError ? error.errorAttributes?.statusCode : undefined;
			if (statusCode === 404) {
				return undefined;
			}
			throw error;
		}
	}

	public updateConversationChannel(
		id: string,
		payload: Partial<ConversationChannel>,
	): Promise<ConversationChannel> {
		// Transform camelCase field names to snake_case for the REST API
		const apiPayload = this.transformCamelCaseToSnakeCase(payload);
		return this.fetchJson<ConversationChannel>({
			path: `/rovo/v1/chat/conversation/${id}`,
			options: {
				method: 'PUT',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
				}),
				body: JSON.stringify(apiPayload),
			},
			holdName: 'rovo-v1-chat-conversation-[id]',
		}).then((response) => this.transformSnakeCaseToCamelCase(response));
	}

	private transformCamelCaseToSnakeCase(
		payload: Partial<ConversationChannel>,
	): Record<string, unknown> {
		const result: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(payload)) {
			if (key === 'readStatus') {
				result['read_status'] = value;
			} else if (key === 'isPinned') {
				result['is_pinned'] = value;
			} else if (key === 'pinnedAt') {
				result['pinned_at'] = value;
			} else if (key === 'sourceMode') {
				result['source_mode'] = value;
			} else {
				result[key] = value;
			}
		}
		return result;
	}

	private transformSnakeCaseToCamelCase(item: unknown): ConversationChannel {
		if (item == null || typeof item !== 'object') {
			throw new Error(`Expected an object from conversation channel API, got: ${typeof item}`);
		}
		const result: Record<string, unknown> = { ...item };
		if ('read_status' in result) {
			result['readStatus'] = result['read_status'];
			delete result['read_status'];
		}
		if ('is_pinned' in result) {
			result['isPinned'] = result['is_pinned'];
			delete result['is_pinned'];
		}
		if ('pinned_at' in result) {
			result['pinnedAt'] = result['pinned_at'];
			delete result['pinned_at'];
		}
		if ('source_mode' in result) {
			result['sourceMode'] = result['source_mode'];
			delete result['source_mode'];
		}

		const agentSessionState = result['agent_session_state'];
		delete result['agent_session_state'];
		delete result['agentSessionState'];
		if (isAgentSessionState(agentSessionState)) {
			result['agentSessionState'] = agentSessionState;
		} else if (agentSessionState != null) {
			logSentryConsoleWarning(
				'Unknown agent session state returned by conversation channel API:',
				agentSessionState,
			);
		}
		return result as ConversationChannel;
	}

	public async resolveConversationAction(
		id: string,
		payload: MessageActionPayload,
	): Promise<MessageActionResponse> {
		return this.fetchJson<MessageActionResponse>({
			path: `/rovo/v1/chat/conversation/${id}/action`,
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
				}),
				body: JSON.stringify({
					...payload,
					context: {
						...payload.context,
						browser_url: window.location.href,
					},
				}),
			},
			holdName: 'rovo-v1-chat-conversation-[id]-action',
		});
	}

	public async bulkResolveConversationUIActions(
		conversationId: string,
		messageId: string,
		payload: BulkMessageActionPayload,
	): Promise<BulkMessageActionResponse> {
		// The bulk API supports a maximum of 50 items per request.
		// Split into batches and process them sequentially.
		const BATCH_SIZE = 50;
		const allResponses: BulkMessageActionResponse = [];

		for (let batchStart = 0; batchStart < payload.items.length; batchStart += BATCH_SIZE) {
			const batchItems = payload.items.slice(batchStart, batchStart + BATCH_SIZE);
			const batchResponses = await this.fetchJson<BulkMessageActionResponse>({
				path: `/rovo/v1/chat/conversation/${conversationId}/message/${messageId}/tools/resolution`,
				options: {
					method: 'POST',
					headers: this.createHeaders({
						'content-type': 'application/json;charset=UTF-8',
					}),
					body: JSON.stringify({
						items: batchItems.map((item) => ({
							...item,
							context: {
								...item.context,
								browser_url: window.location.href,
							},
						})),
					}),
				},
				holdName: 'rovo-v1-chat-conversation-[id]-message-[messageId]-tools-resolution',
			});
			allResponses.push(...batchResponses);
		}

		return allResponses;
	}

	// REST starters, newer API
	public async generateAgentStarters(payload: {
		agent_name?: string;
		description?: string;
		system_prompt?: string;
	}): Promise<ConversationStarterResponse> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};
		return this.fetchJson<ConversationStarterResponse>({
			path: '/rovo/v1/conversation-starters/agent-starter-suggestion',
			options,
			holdName: 'rovo-v1-conversation-starters-agent-starter-suggestion',
		});
	}

	// REST conversation starters, newer API
	public async generateContextualStarters(payload: {
		browser_url: string;
	}): Promise<ConversationStarterResponse> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};
		return this.fetchJson<ConversationStarterResponse>({
			path: '/rovo/v1/conversation-starters/page-contextual',
			options,
			holdName: 'rovo-v1-conversation-starters-page-contextual',
		});
	}

	// REST dynamic conversation starters (new endpoint)
	public async getDynamicConvoStartersNew(
		payload: DynamicConversationStarterRequestNew,
	): Promise<DynamicConversationStarterResponseNew> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
			'x-experience-id': 'convo-starter-for-rovo-button',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};

		return this.fetchJson<DynamicConversationStarterResponseNew>({
			path: '/api/ai/v2/ai-feature/convo-starters',
			options,
			holdName: 'api-ai-v2-ai-feature-convo-starters',
		});
	}

	public async getWebSearchConfiguration(): Promise<WebSearchConfigurationResponse> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers,
		};
		const result = await this.fetchJson<WebSearchConfigurationResponse>({
			path: '/api/v2/configuration/web-search-ai',
			options,
			holdName: 'api-v2-configuration-web-search-ai',
		});

		return result;
	}

	public async getPrompts(): Promise<GetAllPromptsResponse> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers,
		};
		const result = await this.fetchJson<GetAllPromptsResponse>({
			path: '/api/v2/prompts',
			options,
			holdName: 'api-v2-prompts',
		});

		return result;
	}

	public async getPrompt(promptId: string): Promise<GetPromptResponse> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers,
		};
		return this.fetchJson<GetPromptResponse>({
			path: `/api/v2/prompts/${promptId}`,
			options,
			holdName: 'api-v2-prompts-[promptId]',
		});
	}

	public async bulkGetPrompts(payload: BulkGetPromptsRequest): Promise<BulkGetPromptsResponse> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};
		return this.fetchJson<BulkGetPromptsResponse>({
			path: '/api/v2/prompts/bulk-get',
			options,
			holdName: 'api-v2-prompts-bulk-get',
		});
	}

	public async createPrompt(payload: CreatePromptRequest): Promise<GetPromptResponse> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};
		return this.fetchJson<GetPromptResponse>({
			path: '/api/v2/prompts',
			options,
			holdName: 'api-v2-prompts',
		});
	}

	public async updatePrompt(
		promptId: string,
		payload: UpdatePromptRequest,
	): Promise<GetPromptResponse> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'PATCH',
			headers,
			body: JSON.stringify(payload),
		};
		return this.fetchJson<GetPromptResponse>({
			path: `/api/v2/prompts/${promptId}`,
			options,
			holdName: 'api-v2-prompts-update',
		});
	}

	public async addPromptsToLibrary(payload: AddPromptsToLibraryRequest): Promise<void> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};
		await this.fetch({
			path: '/api/v2/prompts/add-to-library',
			options,
			holdName: 'api-v2-prompts-add-to-library',
		});
	}

	/**
	 * Deletes a prompt from the library. `shareId` is the same value as `GetPromptResponse.id`, not a
	 * second kind of identifier: `getPrompt`/`updatePrompt` call the identical value `promptId`, and
	 * the client never holds any other prompt id. See the `deletePrompt` note on `AssistanceService`.
	 */
	public async deletePrompt(shareId: string): Promise<void> {
		const headers = this.createHeaders();
		const options = {
			method: 'DELETE',
			headers,
		};
		await this.fetch({
			path: `/api/v2/prompts/${shareId}`,
			options,
			holdName: 'api-v2-prompts-delete',
		});
	}

	public async favoritePrompts(payload: FavoritePromptsRequest): Promise<void> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};
		await this.fetch({
			path: `/api/v2/prompts/favorite`,
			options,
			holdName: 'api-v2-prompts-favorite',
		});
	}

	public async bulkUpdatePrivacy(
		payload: BulkUpdatePrivacyRequest,
	): Promise<BulkUpdatePrivacyResponse> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};
		return this.fetchJson<BulkUpdatePrivacyResponse>({
			path: '/api/v2/prompts/bulk-update-privacy',
			options,
			holdName: 'api-v2-prompts-bulk-update-privacy',
		});
	}

	public async upsertConversationShare(
		payload: UpsertConversationShareRequest,
	): Promise<ConversationShareResponse> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'PATCH',
			headers,
			body: JSON.stringify(payload),
		};
		return this.fetchJson<ConversationShareResponse>({
			path: '/api/v2/share',
			options,
			holdName: 'api-v2-share-upsert',
		});
	}

	public async ensureConversationShare(
		payload: EnsureConversationShareRequest,
	): Promise<ConversationShareResponse> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'PATCH',
			headers,
			body: JSON.stringify(payload),
		};
		return this.fetchJson<ConversationShareResponse>({
			path: '/api/v2/share',
			options,
			holdName: 'api-v2-share-ensure',
		});
	}

	public async replaceConversationShare(
		payload: ReplaceConversationShareRequest,
	): Promise<ConversationShareResponse> {
		const { conversationId, messageId, shareType, allowedUserAris } = payload;
		const query = new URLSearchParams({ conversationId, messageId });
		const body = {
			conversationId,
			messageId,
			shareType,
			...(shareType === 'SPECIFIC_USERS' ? { allowedUserAris: allowedUserAris ?? [] } : {}),
		};
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'PUT',
			headers,
			body: JSON.stringify(body),
		};
		return this.fetchJson<ConversationShareResponse>({
			path: `/api/v2/share?${query.toString()}`,
			options,
			holdName: 'api-v2-share-replace',
		});
	}

	public async getConversationShare(shareToken: string): Promise<GetConversationShareResponse> {
		return this.fetchJson<GetConversationShareResponse>({
			path: `/api/v2/share/${encodeURIComponent(shareToken)}`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'api-v2-share-get',
		});
	}

	// Defined as an arrow property so `this` stays bound when the reference is
	// passed directly to data-fetching hooks (e.g. useQuery's queryFn).
	public getGroupedConversationShares = async (): Promise<GroupedSharesResponse> => {
		return this.fetchJson<GroupedSharesResponse>({
			path: '/api/v2/shares/grouped',
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'api-v2-shares-grouped-get',
		});
	};

	public async getConversationShareMetadata({
		conversationId,
		messageId,
	}: GetConversationShareMetadataRequest): Promise<GetConversationShareMetadataResponse> {
		const query = new URLSearchParams({ conversationId, messageId });
		return this.fetchJson<GetConversationShareMetadataResponse>({
			path: `/api/v2/share?${query.toString()}`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'api-v2-share-metadata-get',
		});
	}

	public async getConversationSharePreview({
		conversationId,
		messageId,
	}: GetConversationSharePreviewRequest): Promise<GetConversationSharePreviewResponse> {
		const query = new URLSearchParams({ conversationId, messageId });
		return this.fetchJson<GetConversationSharePreviewResponse>({
			path: `/api/v2/share/preview?${query.toString()}`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'api-v2-share-preview-get',
		});
	}

	public async forkSharedConversation(shareToken: string): Promise<ForkConversationShareResponse> {
		return this.fetchJson<ForkConversationShareResponse>({
			path: `/api/v2/share/${encodeURIComponent(shareToken)}/fork`,
			options: {
				method: 'POST',
				headers: this.createHeaders(),
			},
			holdName: 'api-v2-share-fork',
		});
	}

	public async deleteConversationShare(shareToken: string): Promise<void> {
		await this.fetch({
			path: `/api/v2/share/${encodeURIComponent(shareToken)}`,
			options: {
				method: 'DELETE',
				headers: this.createHeaders(),
			},
			holdName: 'api-v2-share-delete',
		});
	}

	public async deleteAllSharesForConversation(conversationId: string): Promise<void> {
		await this.fetch({
			path: `/api/v2/shares/conversation/${encodeURIComponent(conversationId)}`,
			options: {
				method: 'DELETE',
				headers: this.createHeaders(),
			},
			holdName: 'api-v2-shares-conversation-delete',
		});
	}

	public getAgentActions = async (): Promise<AgentAction[]> => {
		const response = await this.fetchJson<{
			actions: AgentAction[];
		}>({
			path: '/rovo/v1/agents/configuration/actions',
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agents-configuration-actions',
		});

		return response.actions;
	};

	public getAgentKnowledgeConfiguration = (
		agentCreatorType?: AgentCreatorType,
	): Promise<AgentKnowledgeConfigurationResponse> => {
		const queryParams = agentCreatorType ? `?agentCreatorType=${agentCreatorType}` : '';

		return this.fetchJson<AgentKnowledgeConfigurationResponse>({
			path: `/rovo/v1/agents/configuration/knowledge${queryParams}`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agents-configuration-knowledge',
		});
	};

	public getAgentPermissions = (
		agentId: string,
		payload: GetAgentPermissionsPayload,
	): Promise<AgentPermissionResponse> => {
		return this.fetchJson({
			path: `/rovo/v2/permissions/agents/${agentId}`,
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
				}),
				body: JSON.stringify(payload),
			},
			holdName: 'rovo-v2-permissions-agents-[agentId]',
		});
	};

	public getRecommendedAgents = ({
		limit,
		pageUrl,
	}: {
		limit: number;
		pageUrl?: string;
	}): Promise<{ agents: Agent[]; metadata: { recommendationKey: string } }> => {
		const urlParams = new URLSearchParams({
			limit: limit.toString(),
		});

		if (pageUrl) {
			urlParams.append('pageUrl', pageUrl);
		}

		const fullPath = `/rovo/v1/agents/recommend/v2?${urlParams.toString()}`;

		return this.fetchJson({
			path: fullPath,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agents-recommend-v2',
		});
	};

	public getConversationDebugLogs = async (conversationId: string): Promise<Chat> => {
		const response = await this.fetchJson<{
			items: Chat;
		}>({
			path: `/rovo/v1/chat/conversation/${conversationId}/debug-logs`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-chat-conversation-[conversationId]-debug-logs',
		});

		return response.items;
	};

	public validateFiles = async (
		payload: FileValidationPayload,
	): Promise<FileValidationResponse> => {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});

		return this.fetchJson<FileValidationResponse>({
			path: this.config.fileValidatePath ?? DEFAULT_FILE_VALIDATE_PATH,
			options: {
				method: 'POST',
				headers,
				body: JSON.stringify(payload),
			},
			holdName: 'rovo-v1-chat-message-file-validate',
		});
	};

	public uploadFiles = async (payload: { files: File[] }): Promise<FileUploadResponse> => {
		const formData = new FormData();

		for (const file of payload.files) {
			formData.append('files', file);
		}

		const response = await this.fetchJson<FileUploadResponse>({
			path: this.config.fileUploadPath ?? DEFAULT_FILE_UPLOAD_PATH,
			options: {
				method: 'POST',
				headers: this.createHeaders(),
				body: formData,
			},
			holdName: 'rovo-v1-chat-message-file',
		});

		return response;
	};

	public rovoLabsSendMessage = (
		payload: RovoLabsChatStreamPayload,
	): Promise<FetchStreamReturn<StreamMessage, false>> => {
		return this.fetchStream<StreamMessage>({
			path: '/rovo/v1/agent-playground/chat/stream',
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
				}),
				body: JSON.stringify(payload),
			},
			holdName: 'rovo-v1-agent-playground-chat-stream',
		});
	};

	public rovoLabsGetConversations = (): Promise<{ items: ConversationChannel[] }> => {
		return this.fetchJson({
			path: '/rovo/v1/agent-playground/chat/conversations',
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agent-playground-chat-conversations',
		});
	};

	public rovoLabsGetTools = (): Promise<{ tools: RovoLabsTool[] }> => {
		return this.fetchJson({
			path: '/rovo/v1/agent-playground/tools',
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agent-playground-tools',
		});
	};

	public rovoLabsGetLanguageModels = (): Promise<{ languageModels: RovoLabsLanguageModel[] }> => {
		return this.fetchJson({
			path: '/rovo/v1/agent-playground/language-models',
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agent-playground-language-models',
		});
	};

	public rovoLabsGetPrompts = (): Promise<RovoLabsDefaultPrompt> => {
		return this.fetchJson({
			path: '/rovo/v1/agent-playground/prompts',
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agent-playground-prompts',
		});
	};

	public getGeneratedContent = (payload?: {
		actions?: string[];
		statuses?: string[];
		limit?: number;
	}): Promise<ConversationContent[]> => {
		const queryParams: string[] = [];
		if (payload?.actions && payload.actions.length > 0) {
			queryParams.push(`actionKeys=${payload.actions.join(',')}`);
		}
		if (payload?.statuses && payload.statuses.length > 0) {
			queryParams.push(`statuses=${payload.statuses.join(',')}`);
		}
		if (payload?.limit !== undefined) {
			queryParams.push(`limit=${payload.limit}`);
		}
		const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
		return this.fetchJson({
			path: `/rovo/v1/chat/generated-content${queryString}`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-chat-generated-content',
		});
	};

	public getRovoUserSettings = async (): Promise<RovoUserSettings> => {
		return this.fetchJson<RovoUserSettings>({
			path: '/rovo/v1/chat/user-settings',
			options: {
				method: 'GET',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
			},
			holdName: 'rovo-v1-chat-user-settings',
		});
	};

	public updateRovoUserSettings = async (
		settings: Partial<RovoUserSettings>,
	): Promise<RovoUserSettings> => {
		return this.fetchJson<RovoUserSettings>({
			path: '/rovo/v1/chat/user-settings',
			options: {
				method: 'PUT',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
				body: JSON.stringify(settings),
			},
			holdName: 'rovo-v1-chat-user-settings',
		});
	};

	public getRovoMemories = async (): Promise<RovoMemoriesResponse> => {
		return this.fetchJson<RovoMemoriesResponse>({
			path: '/rovo/v1/chat/memories',
			options: {
				method: 'GET',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
			},
			holdName: 'rovo-v1-chat-memories',
		});
	};

	public getRovoMemoryBlurb = async (): Promise<RovoMemoryBlurbResponse> => {
		return this.fetchJson<RovoMemoryBlurbResponse>({
			path: '/rovo/v1/chat/memories/blurb',
			options: {
				method: 'GET',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
			},
			holdName: 'rovo-v1-chat-memories-blurb',
		});
	};

	public answerNow = async (conversationId: string, messageId: string): Promise<void> => {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers,
		};
		await this.fetch({
			path: `/rovo/v1/chat/conversation/${conversationId}/message/${messageId}/answer-now`,
			options,
			holdName: 'rovo-v1-chat-conversation-message-answer-now',
		});
	};

	public deleteRovoMemory = async (memoryId: string): Promise<void> => {
		const headers = this.createHeaders();
		const options = {
			method: 'DELETE',
			headers,
		};
		await this.fetch({
			path: `/rovo/v1/chat/memories/${memoryId}`,
			options,
			holdName: 'rovo-v1-chat-memories-[memoryId]',
		});
	};

	public getRovoMemoryEdits = async (): Promise<RovoMemoryEditsResponse> => {
		return this.fetchJson<RovoMemoryEditsResponse>({
			path: '/rovo/v1/chat/memories/memory-edits',
			options: {
				method: 'GET',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
			},
			holdName: 'rovo-v1-chat-memories-memory-edits',
		});
	};

	public createMemoryEdit = async (payload: {
		content: string;
	}): Promise<MemoryEditPromptResponse> => {
		return this.fetchJson<MemoryEditPromptResponse>({
			path: '/rovo/v1/chat/memories/memory-edits',
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
				body: JSON.stringify(payload),
			},
			holdName: 'rovo-v1-chat-memories-memory-edits',
		});
	};

	public createMemory = async (payload: CreateMemoryRequest): Promise<void> => {
		await this.fetch({
			path: '/rovo/v1/chat/memories/create',
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
				body: JSON.stringify(payload),
			},
			holdName: 'rovo-v1-chat-memories-create',
		});
	};

	public getRovoMemoryEditsCount = async (): Promise<RovoMemoryEditsCountResponse> => {
		return this.fetchJson<RovoMemoryEditsCountResponse>({
			path: '/rovo/v1/chat/memories/memory-edits/count',
			options: {
				method: 'GET',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
			},
			holdName: 'rovo-v1-chat-memories-memory-edits-count',
		});
	};

	public deleteRovoMemoryEdit = async (editId: string): Promise<void> => {
		const headers = this.createHeaders();
		const options = {
			method: 'DELETE',
			headers,
		};
		await this.fetch({
			path: `/rovo/v1/chat/memories/memory-edits/${editId}`,
			options,
			holdName: 'rovo-v1-chat-memories-memory-edits-[editId]',
		});
	};

	public fetchConversationMetadata = async (
		conversationId: string,
	): Promise<ConversationChannel> => {
		const result = await this.fetchJson<ConversationChannelResponse>({
			path: `/rovo/v1/chat/conversation/${conversationId}`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-chat-conversation-[conversationId]',
		});

		return this.transformSnakeCaseToCamelCase(result);
	};

	public fetchPersonalizedPromptSuggestions = async (config: {
		offset?: number;
		limit?: number;
		contentType?: ContentType;
		entryPoint?: string;
	}): Promise<SuggestedPromptTile[]> => {
		const { offset = 0, limit = 9, contentType, entryPoint } = config;
		const headers = this.createHeaders({
			'Content-Type': 'application/json',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify({
				ai_feature_input: {
					offset,
					limit,
					...(contentType ? { contentType } : {}),
					...(entryPoint ? { entryPoint } : {}),
				},
			}),
		};
		const result = await this.fetchJson<SuggestedPromptsResponse>({
			path: '/api/ai/v2/ai-feature/confluence-prompt-tiles',
			options,
			holdName: 'api-ai-v2-ai-feature-confluence-prompt-tiles',
		});
		return result?.ai_feature_output?.prompt_tiles ?? [];
	};

	public getTasks = async (params?: {
		limit?: number;
		cursor?: string;
	}): Promise<ApiTaskListResponse> => {
		const searchParams = new URLSearchParams();
		searchParams.set('limit', String(params?.limit ?? TASKS_PAGE_SIZE));
		if (params?.cursor) {
			searchParams.set('cursor', params.cursor);
		}
		return this.fetchJson<ApiTaskListResponse>({
			path: `/rovo/v1/tasks?${searchParams.toString()}`,
			options: {
				method: 'GET',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
			},
			holdName: 'rovo-v1-tasks',
		});
	};

	public getTasksStream = async (
		controller?: AbortController,
	): Promise<FetchStreamReturn<ApiTaskSSEUpdate, true>> => {
		return this.fetchStream<ApiTaskSSEUpdate>(
			{
				path: '/rovo/v1/tasks/stream',
				options: {
					method: 'GET',
					headers: this.createHeaders({
						Accept: 'text/event-stream',
					}),
					signal: controller?.signal,
				},
				holdName: 'rovo-v1-tasks-stream',
			},
			{ useSSE: true },
		);
	};

	public cancelTask = async (taskId: string): Promise<void> => {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});

		const options: RequestInit = {
			method: 'POST',
			headers,
		};

		await this.fetch({
			path: `/rovo/v1/tasks/${taskId}/cancel`,
			options,
			holdName: 'rovo-v1-tasks-[taskId]-cancel',
		});
	};

	public deleteTask = async (taskId: string): Promise<void> => {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});

		const options: RequestInit = {
			method: 'DELETE',
			headers,
		};

		await this.fetch({
			path: `/rovo/v1/tasks/${taskId}`,
			options,
			holdName: 'rovo-v1-tasks-[taskId]-delete',
		});
	};

	public renameTask = async (taskId: string, title: string): Promise<ApiTask> => {
		return this.fetchJson<ApiTask>({
			path: `/rovo/v1/tasks/${taskId}`,
			options: {
				method: 'PUT',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
				body: JSON.stringify({ title }),
			},
			holdName: 'rovo-v1-tasks-[taskId]',
		});
	};

	public fetchPersonalizedPromptSuggestionsStream = async (config: {
		offset?: number;
		limit?: number;
		contentType?: ContentType;
		entryPoint?: string;
		enable3pPrompt?: boolean;
	}): Promise<FetchStreamReturn<SuggestedPromptTileStreamMessage>> => {
		const { offset = 0, limit = 9, contentType, entryPoint, enable3pPrompt } = config;
		const headers = this.createHeaders({
			'Content-Type': 'application/json',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify({
				ai_feature_input: {
					offset,
					limit,
					...(contentType ? { contentType } : {}),
					...(entryPoint ? { entryPoint } : {}),
					// Only sent for experiment variants A/B; omitted for everyone else so the
					// request is byte-identical to the pre-3P-feature request.
					...(enable3pPrompt ? { enable_3p_prompt: true } : {}),
				},
			}),
		};
		return this.fetchStream<SuggestedPromptTileStreamMessage>({
			path: '/api/ai/v2/ai-feature/confluence-prompt-tiles/stream',
			options,
			holdName: 'api-ai-v2-ai-feature-confluence-prompt-tiles-stream',
		});
	};
}

const AssistanceServiceContext = createContext<AssistanceService>(
	new AssistanceServiceImpl({
		baseUrl: '',
		product: '',
		experienceId: '',
	}),
);

export const AssistanceServiceProvider = ({
	value,
	children,
}: {
	value: AssistanceService;
	children: ReactNode;
}): React.JSX.Element => (
	<AssistanceServiceContext.Provider value={value}>{children}</AssistanceServiceContext.Provider>
);

export type UseAssistanceServiceParams = {
	/** If a parent AssistanceServiceProvider is not rendered, this field MUST be provided in order for agents to work */
	product?: AssistanceServiceProduct;
	/** Defaults to `ai-mate` */
	experienceId?: string;
	headers?: Record<string, string> | undefined;
	channelId?: string;
};

/**
 * If these props are provided and one of them are populated, they will take precedence over the `AssistanceServiceProvider` with the missing props filled in
 *
 * @deprecated Please use factory function `createHookWithAssistanceService` instead. We had problems where the consumer of this hook are missing the parent context or not passing in all required paramters to call convo-ai. {@link https://hello.atlassian.net/wiki/spaces/AA6/pages/4323250987/RFC-004+Rovo+components+missing+headers+if+called+outside+chat}
 **/
export const useAssistanceService = (
	props?: UseAssistanceServiceParams,
): AssistanceServiceImpl | AssistanceService => {
	di(useContext, AssistanceServiceImpl);

	const service = useContext(AssistanceServiceContext);

	setRovoChatProduct(props?.product ?? service.getConfig().product);

	if (!props || (!props.experienceId && !props.product && !props.headers && !props.channelId)) {
		return service;
	}

	const product = props.product ?? service.getConfig().product;
	const experienceId = props.experienceId ?? service.getConfig().experienceId;
	const headers = props.headers ?? service.getConfig().headers;
	const channelId = props.channelId ?? service.getConfig().channelId;

	return new AssistanceServiceImpl({
		...service.getConfig(),
		product,
		experienceId,
		headers,
		channelId,
	});
};

type CustomHeaders = Record<Lowercase<string>, string> | undefined;

export type AssistanceServiceInputParam = {
	assistanceServiceParams: {
		/**
		 * This parameter is used as tracing in instrumentation
		 * pass a string that defines where the hook is being called from
		 * make it as granular as possible per touchpoint
		 */
		touchpointSource: string;
		product: AssistanceServiceProduct;
		experienceId: string;
		siteId: string;
		headers?: CustomHeaders;
		channelId?: string;
		baseUrl?: string;
		minionAlias?: string;
		/** @see AssistanceServiceConfig.fileValidatePath */
		fileValidatePath?: string;
		/** @see AssistanceServiceConfig.fileUploadPath */
		fileUploadPath?: string;
	};
};

export type AssistanceServiceInjectedParams = {
	assistanceServiceInjectedParams: {
		experienceTracker: ExperienceTrackerAPI;
		assistanceService: AssistanceService;
		touchpointSource: string;
		product: AssistanceServiceProduct;
		experienceId: string;
		siteId: string;
		headers?: CustomHeaders;
		channelId?: string;
		baseUrl?: string;
	};
};

/**
 * Created for https://hello.atlassian.net/wiki/spaces/AA6/pages/4323250987/RFC-004+Rovo+components+missing+headers+if+called+outside+chat
 * many hooks that require `assistanceService`, often are missing parameters that causes convo-ai call to be 400
 * this hook factory is created to ensure that the `assistanceService` is always called with the correct parameters in any of those hooks
 *
 * Input: react hook that will receive extra prop `assistanceServiceInjectedParams`
 * Output: react hook that enforce `assistanceServiceParams` as a required parameter
 *
 * e.g.
 * const useSomething = createHookWithAssistanceService(
 * 	(hookParams: { customProp: string } & AssistanceServiceInjectedParams) => {
 * 		// this inner hook will get `assistanceServiceInjectedParams` as a prop
 * 		const { customProp, assistanceServiceInjectedParams: { assistanceService } } = hookParams;
 * 		return { ... };
 * 	}
 * );
 *
 * now `useSomething` will have `assistanceServiceParams`, as a required param
 * e.g.
 *
 * useSomething({
 * 	customProp: 'value',
 * 	assistanceServiceParams: { product: 'confluence', experienceId: 'ai-mate', ... }
 * });
 */
export const createHookWithAssistanceService = <P extends AssistanceServiceInjectedParams, R>(
	originalHook: (props: P) => R,
) => {
	return (props: Omit<P, 'assistanceServiceInjectedParams'> & AssistanceServiceInputParam): R => {
		di(AssistanceServiceImpl, useAIMateExperienceTracker);
		const { assistanceServiceParams, ...restProps } = props;

		const experienceTracker = useAIMateExperienceTracker({
			touchpointSource: assistanceServiceParams.touchpointSource,
		});

		const headers: Record<string, string> = useMemo(() => {
			// even though AssistanceServiceInputParam enforces lowercase header keys,
			// it is possible to get a non lowercase key if a prebuilt object was assigned to headers at runtime,
			// so this is an additional safety net, without this duplicate headers keys with different case
			// can get appended during header normalization causing request to fail
			// Relevant thread: https://atlassian.slack.com/archives/C0677M19GU9/p1748531977923069?thread_ts=1747336061.856749&cid=C0677M19GU9
			const inputHeaders = props.assistanceServiceParams.headers
				? Object.fromEntries(
						Object.entries(props.assistanceServiceParams.headers).map(([key, value]) => [
							key.toLowerCase(),
							value,
						]),
					)
				: undefined;
			return {
				'x-cloudid': props.assistanceServiceParams.siteId,
				...inputHeaders,
			};
		}, [props.assistanceServiceParams.siteId, props.assistanceServiceParams.headers]);

		const assistanceService = useMemo(() => {
			return new AssistanceServiceImpl({
				product: assistanceServiceParams.product,
				experienceId: assistanceServiceParams.experienceId,
				headers,
				channelId: assistanceServiceParams.channelId,
				baseUrl: assistanceServiceParams.baseUrl,
				minionAlias: assistanceServiceParams.minionAlias,
				fileValidatePath: assistanceServiceParams.fileValidatePath,
				fileUploadPath: assistanceServiceParams.fileUploadPath,
			});
		}, [
			assistanceServiceParams.baseUrl,
			assistanceServiceParams.experienceId,
			assistanceServiceParams.product,
			assistanceServiceParams.channelId,
			headers,
			assistanceServiceParams.minionAlias,
			assistanceServiceParams.fileValidatePath,
			assistanceServiceParams.fileUploadPath,
		]);

		// eslint-disable-next-line @atlaskit/platform/no-double-type-assertions -- Existing assistance-service baseline: injected hook params are adapted to the generic hook parameter type.
		const result = originalHook({
			...restProps,
			assistanceServiceInjectedParams: {
				...assistanceServiceParams,
				headers,
				experienceTracker,
				assistanceService,
			},
		} as unknown as P);

		return result;
	};
};
